/**
 * Minimal 5-field cron ("minute hour day-of-month month day-of-week"), evaluated against a
 * wall-clock time in the client's timezone. Used for the scheduled emails whose times live in
 * the client profile (`email.dailyDigestCron`, `email.weeklyIntegrityCron`).
 *
 * Supports `*`, numbers, lists (`1,3`), ranges (`1-5`) and steps (`*\/15`, `0-30/10`).
 * Day-of-week 0 and 7 are Sunday. As in classic cron, when both day-of-month and day-of-week
 * are restricted, a time matches if either does.
 */
const FIELDS = [
    { name: 'minute', min: 0, max: 59 },
    { name: 'hour', min: 0, max: 23 },
    { name: 'day of month', min: 1, max: 31 },
    { name: 'month', min: 1, max: 12 },
    { name: 'day of week', min: 0, max: 7 },
];
function parseField(src, f) {
    const set = new Set();
    let star = false;
    for (const part of src.split(',')) {
        const m = /^(\*|(\d+)(?:-(\d+))?)(?:\/(\d+))?$/.exec(part);
        if (!m)
            throw new Error(`Bad cron ${f.name}: "${part}"`);
        const step = m[4] ? Number(m[4]) : 1;
        if (step < 1)
            throw new Error(`Bad cron ${f.name} step: "${part}"`);
        let lo = f.min;
        let hi = f.max;
        if (m[1] === '*') {
            if (!m[4])
                star = true;
        }
        else {
            lo = Number(m[2]);
            hi = m[3] !== undefined ? Number(m[3]) : m[4] ? f.max : lo;
        }
        if (lo < f.min || hi > f.max || lo > hi)
            throw new Error(`Cron ${f.name} out of range: "${part}"`);
        for (let v = lo; v <= hi; v += step)
            set.add(v);
    }
    return { set, star };
}
export function parseCron(expr) {
    const parts = expr.trim().split(/\s+/);
    if (parts.length !== 5)
        throw new Error(`Cron needs 5 fields, got ${parts.length}: "${expr}"`);
    const [min, hour, dom, mon, dow] = parts.map((p, i) => parseField(p, FIELDS[i]));
    if (dow.set.has(7))
        dow.set.add(0);
    return {
        matches(w) {
            if (!min.set.has(w.minute) || !hour.set.has(w.hour) || !mon.set.has(w.month))
                return false;
            const domOk = dom.set.has(w.day);
            const dowOk = dow.set.has(w.weekday);
            if (dom.star || dow.star)
                return (dom.star || domOk) && (dow.star || dowOk);
            return domOk || dowOk;
        },
    };
}
export function isValidCron(expr) {
    try {
        parseCron(expr);
        return true;
    }
    catch {
        return false;
    }
}
//# sourceMappingURL=cron.js.map